import { VideoPlayer } from "./VideoPlayer";
import {
	GamePageResponse,
	GamePageLazyResponse,
	GPGameTeamResponse,
	HighlightHandlers,
	GameClipIntervalRequest,
	GameEventDataExportRequest,
	GameEventFilterUtilityProps,
	GameEventCommentProps,
	RatingPutRequest,
	BadgesPutRequest,
	GPGamePlayerResponse,
	NavOptions,
	PlayerGradingSubmitPayload,
	PlayerBadgesModalMode,
	CoachReviewPromises,
} from "../../types";
import type { ReportEventErrorHandler } from "../../report-error";
import "./video.css";
import { Sidebar } from "./Sidebar";
import { NoVideoMessage } from "./NoVideoMessage";
import { PlayerRatingModal } from "./TeamSheet/PlayerRatingModal";
import { PlayerBadgesModal } from "./TeamSheet/PlayerBadgesModal";
import { AttachClipsBanner } from "./AttachClipsBanner";
import { useFullscreenModalContainer } from "../../useFullscreenModalContainer";
import { UserDownloadActivityTags } from "../../../../../../assets/js/types";
import { useEditStore } from "../../edit-store";
import { useAttachStore } from "../../attach-store";
import { useCoachReviewsStore } from "common/src/gamePage/stores/coach-reviews.store";

interface VideoPlayerProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	highlightsHandlers: HighlightHandlers;
	handleCreateClip: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	handleOpenEditTeamSheetModal: (team: GPGameTeamResponse) => void;
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void;
	gameEventFilters?: GameEventFilterUtilityProps;
	handleTeamSheetDataExport: (type: string) => void;
	gameEventComment?: GameEventCommentProps;
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSetPlayerBadges?: (id: number, badges: BadgesPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	players?: GPGamePlayerResponse[];
	onReportError?: ReportEventErrorHandler;
	handleChangeNavOptions: (tab: NavOptions) => void;
	showCoachToolbar: boolean;
	teamPlayers?: GPGamePlayerResponse[];
	ratingAndBadges?: boolean;
	storeDownloadVideoActivity: (tag: UserDownloadActivityTags, gameId: number) => void;
	toastMessage: (type: string, message: string) => void;
	coachReviewPromises?: CoachReviewPromises;
	userId?: number;
	activeContextTeamId?: number;
}

export function GameVideo({
	gameData,
	gameDataLazy,
	highlightsHandlers,
	handleCreateClip,
	handleOpenEditTeamSheetModal,
	handleGameEventDataExport,
	gameEventFilters,
	handleTeamSheetDataExport,
	gameEventComment,
	handleSetPlayerRating,
	handleSetPlayerBadges,
	handleSubmitPlayerGrading,
	players,
	onReportError,
	handleChangeNavOptions,
	showCoachToolbar,
	teamPlayers,
	ratingAndBadges,
	storeDownloadVideoActivity,
	toastMessage,
	coachReviewPromises,
	userId,
	activeContextTeamId,
}: VideoPlayerProps) {
	const { playerRatingModal, closePlayerRatingModal, playerBadgesModal, closePlayerBadgesModal } = useEditStore();
	const upsertDraft = useCoachReviewsStore((state) => state.upsertDraft);
	const { target: attachTarget } = useAttachStore();
	const fullscreenModalContainer = useFullscreenModalContainer();

	const handlePlayerBadgesSubmit = (playerId: number, badges: BadgesPutRequest) => {
		if (playerBadgesModal.mode === PlayerBadgesModalMode.Attach) {
			// If no userId, don't allow draft upsert
			if (userId === undefined) {
				return;
			}

			const newBadgeIds = badges.badgeIds || [];

			upsertDraft({
				userId: userId,
				gameId: gameData.game.id,
				kind: "player",
				playerId,
				assignedBadgeId: newBadgeIds[0] || null, // the first badge ID, as only one badge can be attached at a time
			});

			return;
		}

		handleSetPlayerBadges?.(playerId, badges);
	};

	return (
		<div className="gameVideo-container">
			{attachTarget !== null && userId && <AttachClipsBanner gameData={gameData} userId={userId} />}
			{showCoachToolbar && (
				<>
					{ratingAndBadges && (
						<PlayerRatingModal
							key={`rating-${playerRatingModal.show}-${playerRatingModal.id}`}
							modalData={{ ...playerRatingModal, players: teamPlayers }}
							close={closePlayerRatingModal}
							onSubmit={handleSetPlayerRating}
							showPlayersSelect={true}
							players={teamPlayers}
							UNSTABLE_portalContainer={fullscreenModalContainer}
						/>
					)}
					<PlayerBadgesModal
						key={`badges-${playerBadgesModal.show}-${playerBadgesModal.id}-${playerBadgesModal.mode}`}
						badges={gameData.ratingBadges}
						modalData={{ ...playerBadgesModal, players: teamPlayers }}
						close={closePlayerBadgesModal}
						onSubmit={handlePlayerBadgesSubmit}
						maxBadgeCount={playerBadgesModal.mode === PlayerBadgesModalMode.Attach ? 1 : undefined}
						showPlayersSelect={playerBadgesModal.mode === PlayerBadgesModalMode.Save}
						players={teamPlayers}
						UNSTABLE_portalContainer={fullscreenModalContainer}
					/>
				</>
			)}

			<div className="gameVideo-body">
				{gameData?.video?.url ? (
					<VideoPlayer
						handleCreateClip={handleCreateClip}
						gameData={gameData}
						gameDataLazy={gameDataLazy}
						showCoachingTools={showCoachToolbar}
						highlightsHandlers={highlightsHandlers}
						handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
						handleGameEventDataExport={handleGameEventDataExport}
						handleTeamSheetDataExport={handleTeamSheetDataExport}
						handleChangeNavOptions={handleChangeNavOptions}
						storeDownloadVideoActivity={storeDownloadVideoActivity}
						handleSetPlayerRating={handleSetPlayerRating}
						handleSubmitPlayerGrading={handleSubmitPlayerGrading}
						toastMessage={toastMessage}
						gameEventComment={gameEventComment}
						players={players}
						onReportError={onReportError}
						coachReviewPromises={coachReviewPromises}
						userId={userId}
						activeContextTeamId={activeContextTeamId}
					/>
				) : (
					<NoVideoMessage />
				)}
				<Sidebar
					gameData={gameData}
					gameDataLazy={gameDataLazy}
					showCoachingTools={showCoachToolbar}
					highlightsHandlers={highlightsHandlers}
					handleCreateClip={handleCreateClip}
					handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
					handleGameEventDataExport={handleGameEventDataExport}
					gameEventFilters={gameEventFilters}
					handleTeamSheetDataExport={handleTeamSheetDataExport}
					handleSetPlayerRating={handleSetPlayerRating}
					handleSubmitPlayerGrading={handleSubmitPlayerGrading}
					toastMessage={toastMessage}
					gameEventComment={gameEventComment}
					players={players}
					onReportError={onReportError}
					coachReviewPromises={coachReviewPromises}
					userId={userId}
					activeContextTeamId={activeContextTeamId}
				/>
			</div>
		</div>
	);
}
